import type {
  ComponentRequirements,
  DataTokenRequirement,
  DataFieldPath,
  DataTokenCategory,
} from '@vibesites/tokens';
import { defineComponentRequirements } from '@vibesites/tokens';

// ---------------------------------------------------------------------------
// Data-token helpers
//
// The parent `defineComponentRequirements(componentName, requirements)` helper
// expects the canonical `ComponentRequirements` shape: `designTokens` (CSS
// token paths) and `dataTokens` (an array of `{ field, required, category }`
// records consumed by `@vibesites/tokens` validator.ts). The helpers below let
// each component keep declaring plain field/collection name lists while we map
// them onto that canonical shape in one place.
//
// NOTE (parent-theme limitation): several property fields this theme relies on
// are not yet part of the parent `DataFieldPath` union (e.g. property_tagline,
// the pet-policy fields, and the *_blurb variants). We cast through
// `DataFieldPath` in the single `toDataToken` helper to preserve the data;
// widening the union belongs in @vibesites/tokens.
// ---------------------------------------------------------------------------

const FIELD_CATEGORY: Record<string, DataTokenCategory> = {
  // contact
  property_phone: 'contact',
  property_call_tracking_phone: 'contact',
  property_email: 'contact',
  property_full_address: 'contact',
  property_address: 'contact',
  property_address_city: 'contact',
  property_address_state: 'contact',
  property_city_state_zip: 'contact',
  // branding
  property_name: 'branding',
  property_primary_logo: 'branding',
  property_secondary_logo: 'branding',
  property_mono_black_logo: 'branding',
  property_mono_white_logo: 'branding',
  property_company_mono_white_logo: 'branding',
  property_company_mono_black_logo: 'branding',
  property_company_url: 'branding',
  // urls
  property_apply_now_url: 'urls',
  property_resident_portal_url: 'urls',
  property_floorplans_url: 'urls',
  property_website_url: 'urls',
  property_virtual_tour_url: 'urls',
  property_schedule_tour_url: 'urls',
  property_facebook_url: 'urls',
  property_instagram_url: 'urls',
  // details
  property_bedrooms_range: 'details',
  property_ispetfriendly: 'details',
  property_allows_cats: 'details',
  property_allows_dogs: 'details',
  property_max_pets: 'details',
  property_max_pet_weight_limit: 'details',
  property_pet_deposit: 'details',
  property_upfront_pet_fee: 'details',
  property_monthly_pet_rent: 'details',
  // content
  property_short_description: 'content',
  property_tagline: 'content',
  property_description: 'content',
  property_map_image: 'content',
  property_neighborhood_description: 'content',
  property_pet_restrictions: 'content',
  property_pet_policy_description: 'content',
  property_petpolicy_info: 'content',
  property_floorplans_blurb: 'content',
  property_amenities_blurb: 'content',
  property_gallery_blurb: 'content',
  property_neighborhood_blurb: 'content',
  property_virtualtours_blurb: 'content',
  property_contactus_blurb: 'content',
  property_reviews_blurb: 'content',
  property_pets_blurb: 'content',
  property_residentaccess_blurb: 'content',
  property_scheduletour_blurb: 'content',
  property_interactivemap_blurb: 'content',
};

function toDataToken(field: string, category: DataTokenCategory): DataTokenRequirement {
  return { field: field as DataFieldPath, required: false, category };
}

/** Build the `dataTokens` array from plain field + collection name lists. */
function dataTokens(fields: string[] = [], collections: string[] = []): DataTokenRequirement[] {
  return [
    ...fields.map((f) => toDataToken(f, FIELD_CATEGORY[f] ?? 'content')),
    ...collections.map((c) => toDataToken(c, 'collections')),
  ];
}

export const componentRequirements: Record<string, ComponentRequirements> = {
  Header: defineComponentRequirements('Header', {
    designTokens: ['color.primary', 'color.secondary', 'color.accent', 'color.text', 'color.textMuted', 'color.surface', 'color.border', 'font.heading', 'font.body'],
    dataTokens: dataTokens(['property_phone', 'property_full_address', 'property_mono_black_logo', 'property_secondary_logo', 'property_primary_logo', 'property_apply_now_url', 'property_resident_portal_url']),
  }),
  Hero: defineComponentRequirements('Hero', {
    designTokens: ['color.primary', 'color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens(['property_short_description', 'property_floorplans_url', 'property_bedrooms_range', 'property_address_city', 'property_address_state'], ['photo_gallery']),
  }),
  PropertyOverview: defineComponentRequirements('PropertyOverview', {
    designTokens: ['color.primary', 'color.accent', 'color.text', 'color.surface', 'font.heading', 'font.body'],
    dataTokens: dataTokens(['property_tagline', 'property_description'], ['photo_gallery']),
  }),
  SpecialOffers: defineComponentRequirements('SpecialOffers', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens(['property_website_url'], ['photo_gallery', 'special_offers']),
  }),
  CTASection: defineComponentRequirements('CTASection', {
    designTokens: ['color.text', 'color.surface', 'font.heading', 'font.body'],
    dataTokens: dataTokens(['property_name', 'property_address_city'], ['photo_gallery']),
  }),
  TabbedCallouts: defineComponentRequirements('TabbedCallouts', {
    designTokens: ['color.text', 'color.surface', 'font.heading', 'font.body'],
    dataTokens: dataTokens(['property_name', 'property_address_city'], ['photo_gallery']),
  }),
  FloorplansList: defineComponentRequirements('FloorplansList', {
    designTokens: ['color.primary', 'color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['floorplans', 'photo_gallery']),
  }),
  PetPolicy: defineComponentRequirements('PetPolicy', {
    designTokens: ['color.primary', 'color.text', 'color.textMuted', 'color.surface', 'color.border', 'color.background', 'font.heading', 'font.body'],
    dataTokens: dataTokens([
      'property_ispetfriendly', 'property_allows_cats', 'property_allows_dogs',
      'property_max_pets', 'property_max_pet_weight_limit',
      'property_pet_deposit', 'property_upfront_pet_fee', 'property_monthly_pet_rent',
      'property_pet_restrictions', 'property_pet_policy_description', 'property_petpolicy_info',
    ]),
  }),
  AmenitiesList: defineComponentRequirements('AmenitiesList', {
    designTokens: ['color.primary', 'color.text', 'color.surface', 'color.border', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['amenities', 'photo_gallery']),
  }),
  GalleryApartments: defineComponentRequirements('GalleryApartments', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  GallerySeparator: defineComponentRequirements('GallerySeparator', {
    designTokens: ['color.primary', 'color.accent', 'color.text', 'font.heading'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  GalleryCommunity: defineComponentRequirements('GalleryCommunity', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  NeighborhoodMap: defineComponentRequirements('NeighborhoodMap', {
    designTokens: ['color.primary', 'color.text', 'color.surface', 'color.border', 'font.heading', 'font.body'],
    dataTokens: dataTokens(['property_map_image', 'property_neighborhood_description'], ['neighborhood_pois']),
  }),
  VirtualTourEmbed: defineComponentRequirements('VirtualTourEmbed', {
    designTokens: ['color.text', 'color.surface', 'font.heading', 'font.body'],
    dataTokens: dataTokens(['property_virtual_tour_url']),
  }),
  ContactForm: defineComponentRequirements('ContactForm', {
    designTokens: ['color.primary', 'color.text', 'color.surface', 'font.heading', 'font.body'],
    dataTokens: dataTokens(['property_phone', 'property_address', 'property_city_state_zip', 'property_email'], ['photo_gallery']),
  }),
  Fees: defineComponentRequirements('Fees', {
    designTokens: ['color.primary', 'color.text', 'color.surface', 'color.border', 'font.heading', 'font.body'],
    dataTokens: dataTokens(),
  }),
  InteractiveMapHero: defineComponentRequirements('InteractiveMapHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  FloorPlansHero: defineComponentRequirements('FloorPlansHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  VirtualTourHero: defineComponentRequirements('VirtualTourHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  AmenitiesHero: defineComponentRequirements('AmenitiesHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  PetPolicyHero: defineComponentRequirements('PetPolicyHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  GalleryHero: defineComponentRequirements('GalleryHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  NeighborhoodHero: defineComponentRequirements('NeighborhoodHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  ContactHero: defineComponentRequirements('ContactHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  ResidentAccessHero: defineComponentRequirements('ResidentAccessHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  ScheduleATourHero: defineComponentRequirements('ScheduleATourHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  ReviewsHero: defineComponentRequirements('ReviewsHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  PrivacyPolicyHero: defineComponentRequirements('PrivacyPolicyHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  SitemapHero: defineComponentRequirements('SitemapHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  AccessibilityHero: defineComponentRequirements('AccessibilityHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  ApplyNowHero: defineComponentRequirements('ApplyNowHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  ThankYouHero: defineComponentRequirements('ThankYouHero', {
    designTokens: ['color.text', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['photo_gallery']),
  }),
  PageBlurb: defineComponentRequirements('PageBlurb', {
    designTokens: ['color.text', 'color.textMuted', 'font.body'],
    dataTokens: dataTokens([
      'property_floorplans_blurb', 'property_amenities_blurb', 'property_gallery_blurb',
      'property_neighborhood_blurb', 'property_virtualtours_blurb', 'property_contactus_blurb',
      'property_reviews_blurb', 'property_pets_blurb', 'property_residentaccess_blurb',
      'property_scheduletour_blurb', 'property_interactivemap_blurb',
    ]),
  }),
  Reviews: defineComponentRequirements('Reviews', {
    designTokens: ['color.primary', 'color.text', 'color.surface', 'color.border', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['reviews']),
  }),
  SpecialOfferBanner: defineComponentRequirements('SpecialOfferBanner', {
    designTokens: ['color.primary', 'font.heading', 'font.body'],
    dataTokens: dataTokens([], ['special_offers']),
  }),
  Footer: defineComponentRequirements('Footer', {
    designTokens: ['color.text', 'color.border', 'font.heading', 'font.body'],
    dataTokens: dataTokens(['property_call_tracking_phone', 'property_full_address', 'property_mono_white_logo', 'property_mono_black_logo', 'property_company_mono_white_logo', 'property_company_mono_black_logo', 'property_company_url', 'property_facebook_url', 'property_instagram_url'], ['office_hours']),
  }),
  Navigation: defineComponentRequirements('Navigation', {
    designTokens: ['color.primary', 'color.secondary', 'color.text', 'color.surface', 'color.border', 'font.body'],
    dataTokens: dataTokens(['property_schedule_tour_url']),
  }),
};

export function getComponentRequirements(componentName: string): ComponentRequirements | undefined {
  return componentRequirements[componentName];
}

export function getRegisteredComponentNames(): string[] {
  return Object.keys(componentRequirements);
}
